import { faListCheck } from '@fortawesome/pro-solid-svg-icons'
import clsx from 'clsx'
import { Fragment, type ReactElement, useCallback, useMemo } from 'react'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import type { Action } from '~/domains/actions/types'
import { useInfiniteList } from '~/hooks/useInfiniteList'
import { groupTasksByDue } from '../../utils/groupTasksByDue'
import { TaskCard } from '../TaskCard/TaskCard'
import {
  taskGrid,
  taskGridFullRow,
  taskGridSectionTitle,
  taskGridTreated
} from '../TaskCard/TaskCard.css'
import {
  taskSectionsLoader,
  taskSectionTitle,
  taskSectionTitleOverdue
} from './TaskSections.css'

type TaskSectionsProps = {
  tasks: Action[]
  isLoading: boolean
  isError: boolean
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  fetchNextPage?: () => void
  emptyMessage?: string
  onResetFilters?: () => void
  /** Reference time for the due sections; pass a ticking clock so tasks move to "En retard" on time. */
  now?: Date
  /** Treated tasks: flat list in the order received (most recently treated first), completion date shown. */
  treated?: boolean
}

export const TaskSections = ({
  tasks,
  isLoading,
  isError,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  emptyMessage = 'Aucune tâche à faire',
  onResetFilters,
  now,
  treated = false
}: TaskSectionsProps): ReactElement => {
  const sections = useMemo(
    () =>
      treated
        ? [{ key: 'treated', label: null, tasks }]
        : groupTasksByDue(tasks, now),
    [tasks, now, treated]
  )
  const endOfListReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage?.()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])
  const { lastItemRef } = useInfiniteList({
    onEndOfListReached: endOfListReached,
    enabled: hasNextPage
  })

  if (isError) {
    return (
      <NoResult
        isError
        icon={faListCheck}
        message="Impossible de charger les tâches"
      />
    )
  }

  if (isLoading) {
    return (
      <div className={taskSectionsLoader}>
        <Shimmer width="100%" height={60} />
        <Shimmer width="100%" height={60} />
        <Shimmer width="100%" height={60} />
      </div>
    )
  }

  if (tasks.length === 0) {
    return (
      <NoResult
        icon={faListCheck}
        message={emptyMessage}
        onDismiss={onResetFilters}
        dismissLabel={onResetFilters ? 'Réinitialiser les filtres' : undefined}
      />
    )
  }

  const lastTaskId = tasks[tasks.length - 1]?.id

  return (
    <div className={treated ? taskGridTreated : taskGrid}>
      {sections.map((section) => (
        <Fragment key={section.key}>
          {section.label !== null && (
            <h2
              className={clsx(
                taskGridSectionTitle,
                taskSectionTitle,
                section.key === 'overdue' && taskSectionTitleOverdue
              )}
            >
              {section.label}
            </h2>
          )}
          {section.tasks.map((task) => (
            <TaskCard key={task.id} task={task} showCompletion={treated} />
          ))}
          {section.tasks.some((task) => task.id === lastTaskId) && (
            <div ref={lastItemRef} className={taskGridFullRow} />
          )}
        </Fragment>
      ))}
      {isFetchingNextPage && (
        <div className={taskGridFullRow}>
          <Shimmer width="100%" height={60} />
        </div>
      )}
    </div>
  )
}
